// Layouts/Agency/AgencySetup.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import { agencyStore } from '../../stateManagers/stores/agencyStore';
import { userProfileStore } from '../../stateManagers/stores/userProfileStore';
import { useAuth } from '../../stateManagers/services/AuthContext';

const C = {
  teal: '#115550',
  tealMid: '#16706a',
  lime: '#c8e94b',
  border: '#E5E7EB',
  muted: '#9ca3af',
  sub: '#6b7280',
};

const AgencySetup: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const { logout } = useAuth();

  const { setupAgency, loading, error, issues, clearError } = agencyStore();
  const { profile, fetchProfile } = userProfileStore();

  const [form, setForm] = useState({
    name: '',
    description: '',
    state: '',
    city: '',
    pincode: '',
  });

  // If user already has agency, send them away
  useEffect(() => {
    if (profile?.agency_id) {
      navigate('/dashboard', { replace: true });
    }
  }, [profile?.agency_id, navigate]);

  useEffect(() => {
    if (error && toast.current) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: issues.length ? issues.join(' ') : error,
        life: 4000,
      });
      clearError();
    }
  }, [error, issues, clearError]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = await setupAgency({
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      state: form.state.trim(),
      city: form.city.trim(),
      pincode: form.pincode.trim(),
    });

    if (result.success) {
      toast.current?.show({
        severity: 'success',
        summary: 'Agency created',
        detail: `Code: ${result.agency_code}. Please login again to refresh your session.`,
        life: 4000,
      });

      // Backend requires re-login for session to pick up agency_id
      setTimeout(async () => {
        await logout();
        navigate('/');
      }, 2500);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    border: `1px solid ${C.border}`,
    borderRadius: '10px',
    fontSize: '0.9rem',
    color: C.teal,
    outline: 'none',
    background: '#f8fffe',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: C.sub,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  };

  return (
    <>
      <Toast ref={toast} />

      <div
        style={{
          minHeight: 'calc(100vh - 75px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '32px 20px',
          background: 'linear-gradient(160deg, #f4faf9 0%, #e8f5f3 100%)',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '520px',
            background: 'white',
            borderRadius: '16px',
            border: `1px solid ${C.border}`,
            boxShadow: '0 12px 40px rgba(17,85,80,0.12)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              background: `linear-gradient(135deg, ${C.teal} 0%, ${C.tealMid} 100%)`,
              padding: '28px 28px 24px',
              color: 'white',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: C.lime,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '14px',
              }}
            >
              <i className="pi pi-building" style={{ fontSize: '1.3rem', color: C.teal }} />
            </div>
            <h1 style={{ margin: '0 0 6px', fontSize: '1.35rem', fontWeight: 700 }}>
              Set up your Agency
            </h1>
            <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.85 }}>
              Complete this once to unlock the full dashboard.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ padding: '28px' }}>
            <div style={{ marginBottom: '18px' }}>
              <label style={labelStyle}>Agency Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                placeholder="e.g. North Zone Education Hub"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: '18px' }}>
              <label style={labelStyle}>Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={3}
                placeholder="Optional short description"
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
              <div>
                <label style={labelStyle}>State *</label>
                <input
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Maharashtra"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>City *</label>
                <input
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Pune"
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Pincode *</label>
              <input
                name="pincode"
                value={form.pincode}
                onChange={handleChange}
                required
                maxLength={6}
                pattern="\d{6}"
                placeholder="6-digit pincode"
                style={inputStyle}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: loading ? C.muted : C.teal,
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: loading ? 'none' : '0 4px 14px rgba(17,85,80,0.35)',
                transition: 'all 0.2s',
              }}
            >
              {loading ? (
                <>
                  <i className="pi pi-spin pi-spinner" /> Creating…
                </>
              ) : (
                <>
                  <i className="pi pi-check" /> Create Agency
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default AgencySetup;